import type { PoseParams } from './types';
import { renderPoseSvg, svgToDataUrl } from './render';
import { uploadFile } from '@/lib/upload';
import { nanoid } from 'nanoid';

export function buildPoseImageNodeDataFromUrl({
  url,
  now,
}: {
  url: string;
  now: string;
}): Record<string, unknown> {
  return {
    name: 'Pose',
    // Keep as primitive image node by default (no incomers, no viewMode: 'transform')
    content: { url, type: 'image/png' },
    sourceUrl: url,
    // Ensure square aspect ratio for newly created node (ImagePrimitive falls back to 16/9 when size is missing)
    size: '1024x1024',
    updatedAt: now,
  };
}

async function svgToPngBlob(svg: string, size: number): Promise<Blob> {
  // Browser-only conversion (ToolNode runs client-side)
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const svgUrl = URL.createObjectURL(blob);

  try {
    const img = new Image();
    img.decoding = 'async';
    img.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load SVG image'));
      img.src = svgUrl;
    });

    const scale = 2; // crisper output
    const canvas = document.createElement('canvas');
    canvas.width = size * scale;
    canvas.height = size * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2D context');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.drawImage(img, 0, 0, size, size);

    const png = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('PNG encode failed'))),
        'image/png',
        0.92
      );
    });

    return png;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

async function poseToPngDataUrl(params: PoseParams, size = 768): Promise<string> {
  // Prefer true PNG data url.
  const svg = renderPoseSvg(params.joints, size);
  const pngBlob = await svgToPngBlob(svg, size);
  const arrayBuffer = await pngBlob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  const b64 = btoa(binary);
  return `data:image/png;base64,${b64}`;
}

export async function buildPoseImageNodeDataDataUrl({
  params,
  now,
}: {
  params: PoseParams;
  now: string;
}): Promise<Record<string, unknown>> {
  // If canvas APIs are unavailable, fall back to SVG data URL.
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      const svg = renderPoseSvg(params.joints, 768);
      const url = svgToDataUrl(svg);
      return {
        name: 'Pose',
        content: { url, type: 'image/svg+xml' },
        sourceUrl: url,
        updatedAt: now,
      };
    }
    const url = await poseToPngDataUrl(params, 768);
    return buildPoseImageNodeDataFromUrl({ url, now });
  } catch {
    const svg = renderPoseSvg(params.joints, 768);
    const url = svgToDataUrl(svg);
    return {
      name: 'Pose',
      content: { url, type: 'image/svg+xml' },
      sourceUrl: url,
      updatedAt: now,
    };
  }
}

export async function uploadPosePngAndGetUrl({
  params,
  auth,
}: {
  params: PoseParams;
  auth: { token: string; userId: string; projectId?: string };
}): Promise<{ url: string; type: string }> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('PNG upload requires browser context');
  }
  const svg = renderPoseSvg(params.joints, 768);
  const pngBlob = await svgToPngBlob(svg, 768);
  const file = new File([pngBlob], `pose_${nanoid()}.png`, { type: 'image/png' });
  // Use 'files' bucket with project-scoped dir and assetType=image (consistent with other assets)
  return await uploadFile(file, 'files', {
    token: auth.token,
    userId: auth.userId,
    projectId: auth.projectId,
    assetType: 'image',
  });
}


